import { toast } from "react-toastify";
import "./alert.css";
import React, { useState } from "react";
import { useCoinRedeemMutation } from "../../redux/rtk/apiRtk";
import { FaCoins, FaMoneyBillWave, FaTimes, FaWallet } from "react-icons/fa";

export const ConfirmationAlert = ({ message, onConfirm, onCancel }) => {
  return (
    <div className="overlay">
      <div className="modal">
        <h2>{message}</h2>
        <div className="button-group">
          <button className="button" onClick={onConfirm}>
            Yes
          </button>
          <button className="button" onClick={onCancel}>
            No
          </button>
        </div>
      </div>
    </div>
  );
};

export const RedeemModal = ({ isOpen, onClose, fullAmount, onRedeem }) => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  // RTK Query mutation hook
  const [coinRedeem, { isLoading: isRedeeming }] = useCoinRedeemMutation();

  const handleRedeemRequest = async () => {
    const redeemAmount = parseInt(amount);
    if (isNaN(redeemAmount) || redeemAmount <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    if (redeemAmount > fullAmount) {
      setError(`Amount cannot exceed ₹${fullAmount}`);
      return;
    }

    setError("");

    try {
      await coinRedeem(redeemAmount).unwrap();
      toast.success("Redeem request submitted successfully!");
      onRedeem();
      onClose();
      setAmount("");
    } catch (error) {
      console.error("Redeem error:", error);
      toast.error(error?.data?.message || "Failed to submit redeem request");
    }
  };

  const handleRedeemFullAmount = () => {
    setAmount(fullAmount.toString());
    setError("");
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (
      value === "" ||
      (Number.isInteger(Number(value)) && Number(value) >= 0)
    ) {
      setAmount(value);
      setError("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 sm:mx-0 transform transition-all duration-300 scale-100">
        {/* Decorative Header Background */}
        <div className="relative overflow-hidden rounded-t-2xl bg-gradient-to-r from-emerald-500 to-teal-600 p-6">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>

          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm">
                <FaWallet className="text-white text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Redeem Funds</h2>
                <p className="text-emerald-100 text-sm">
                  Withdraw your earnings
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-all duration-200"
              disabled={isRedeeming}
            >
              <FaTimes className="text-lg" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          {/* Available Balance Card */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-100 rounded-full">
                  <FaCoins className="text-emerald-600 text-lg" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-700">
                    Available Balance
                  </p>
                  <p className="text-2xl font-bold text-emerald-800">
                    ₹ {fullAmount.toFixed(0)}
                  </p>
                </div>
              </div>
              <div className="text-emerald-600">
                <FaMoneyBillWave className="text-2xl opacity-60" />
              </div>
            </div>
          </div>

          {/* Amount Input */}
          <div className="mb-6">
            <label
              htmlFor="amount"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Enter Amount to Redeem
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 text-lg">₹</span>
              </div>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={handleAmountChange}
                className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 text-lg font-medium"
                placeholder="0"
                min="0"
                max={fullAmount}
                disabled={isRedeeming}
              />
            </div>
            {error && (
              <div className="mt-2 flex items-center space-x-2 text-red-600">
                <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="text-xs">!</span>
                </div>
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}
          </div>

          {/* Quick Action Button */}
          <button
            onClick={handleRedeemFullAmount}
            className="w-full py-3 px-4 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 font-semibold rounded-xl hover:from-emerald-200 hover:to-teal-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all duration-200 border border-emerald-200 mb-6"
            disabled={isRedeeming}
          >
            <div className="flex items-center justify-center space-x-2">
              <FaCoins className="text-sm" />
              <span>Redeem Full Amount (₹{fullAmount.toFixed(0)})</span>
            </div>
          </button>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 text-gray-600 bg-gray-100 font-semibold rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
              disabled={isRedeeming}
            >
              Cancel
            </button>
            <button
              onClick={handleRedeemRequest}
              disabled={isRedeeming || !amount || parseInt(amount) <= 0}
              className="flex-1 py-3 px-4 text-white bg-gradient-to-r from-emerald-500 to-teal-600 font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
            >
              <div className="flex items-center justify-center space-x-2">
                {isRedeeming ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <FaMoneyBillWave className="text-sm" />
                    <span>Redeem Now</span>
                  </>
                )}
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
